import { Box, Input, Radio, RadioGroup, SimpleGrid, Stack, Text } from '@chakra-ui/react'

import { FormField } from '../../../../components/FormField'
import ImageCropInput from '../../../../components/ImageCropInput/ImageCropInput'
import { MaskedInput, PIX_KEY_MASKS, useZipCodeLookup } from '../../../../components/MaskedInput'
import { SectionTitle } from '../../../../components/SectionCard'
import { FormSelect } from '../../../../components/Select'
import { capitalizeName, PixKeyType } from '../../../../helpers'
import {
  formatBankAccount,
  IAdmissionStepProps,
  MARITAL_STATUS_OPTIONS,
  PIX_KEY_TYPE_OPTIONS,
  RACE_OPTIONS,
  STATES
} from '../admissionForm'

interface IPersonalStepProps extends IAdmissionStepProps {
  photo: File | null
  photoUrl: string | null
  onPhotoChange: (photo: File) => void
}

// The profile photo is cropped square and shrunk in the browser before it is sent.
const PHOTO_SIZE = 800

export default function PersonalStep({
  form,
  update,
  errors,
  isDisabled,
  mode = 'form',
  photo,
  photoUrl,
  onPhotoChange
}: IPersonalStepProps) {
  const required = mode === 'form'
  const { loadingZipCode, lookup } = useZipCodeLookup({
    onAddressFound: (address) => update(address)
  })

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormField label="Nome completo" error={errors.name} isRequired>
          <Input
            value={form.name}
            onChange={(e) => update({ name: e.target.value })}
            onBlur={(e) => update({ name: capitalizeName(e.target.value) })}
            autoComplete="name"
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Como gostaria de ser chamado(a)" error={errors.nickname} isRequired={required}>
          <Input value={form.nickname} onChange={(e) => update({ nickname: e.target.value })} isDisabled={isDisabled} />
        </FormField>
        <FormField
          label="E-mail Google"
          error={errors.email}
          isRequired
          helperText={
            required ? 'O e-mail da sua conta Google. O link para continuar depois também vai para ele.' : undefined
          }
        >
          <MaskedInput
            mask="email"
            value={form.email}
            onChange={(email) => update({ email })}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Telefone" error={errors.phone} isRequired={required}>
          <MaskedInput
            mask="phone"
            value={form.phone}
            onChange={(phone) => update({ phone })}
            isDisabled={isDisabled}
          />
        </FormField>
      </SimpleGrid>

      <SectionTitle mt={8}>Endereço</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormField
          label="CEP"
          error={errors.zip_code}
          isRequired={required}
          helperText={loadingZipCode ? 'Buscando endereço...' : undefined}
        >
          <MaskedInput
            mask="zipCode"
            value={form.zip_code}
            onChange={(zipCode) => {
              update({ zip_code: zipCode })
              lookup(zipCode)
            }}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Rua" error={errors.street} isRequired={required} gridColumn={{ md: 'span 2' }}>
          <Input value={form.street} onChange={(e) => update({ street: e.target.value })} isDisabled={isDisabled} />
        </FormField>
        <FormField label="Número" error={errors.number} isRequired={required}>
          <Input value={form.number} onChange={(e) => update({ number: e.target.value })} isDisabled={isDisabled} />
        </FormField>
        <FormField label="Complemento" error={errors.complement} gridColumn={{ md: 'span 2' }}>
          <Input
            value={form.complement}
            onChange={(e) => update({ complement: e.target.value })}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Bairro" error={errors.neighborhood} isRequired={required}>
          <Input
            value={form.neighborhood}
            onChange={(e) => update({ neighborhood: e.target.value })}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Cidade" error={errors.city} isRequired={required}>
          <Input value={form.city} onChange={(e) => update({ city: e.target.value })} isDisabled={isDisabled} />
        </FormField>
        <FormField label="UF" error={errors.state} isRequired={required}>
          <FormSelect
            value={form.state}
            onChange={(state) => update({ state })}
            options={STATES.map((state) => ({ value: state, label: state }))}
            isDisabled={isDisabled}
          />
        </FormField>
      </SimpleGrid>

      <SectionTitle mt={8}>Conta Inter</SectionTitle>
      <Text variant="caption" mb={3}>
        O salário é pago no Banco Inter (077).
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormField label="Agência" error={errors.bank_agency} isRequired={required}>
          <Input
            value={form.bank_agency}
            onChange={(e) => update({ bank_agency: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            inputMode="numeric"
            placeholder="0001"
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Conta com dígito" error={errors.bank_account} isRequired={required}>
          <Input
            value={form.bank_account}
            onChange={(e) => update({ bank_account: formatBankAccount(e.target.value) })}
            inputMode="numeric"
            placeholder="1234567-8"
            isDisabled={isDisabled}
          />
        </FormField>
      </SimpleGrid>

      <SectionTitle mt={8}>Chave Pix</SectionTitle>
      <Text variant="caption" mb={3}>
        Opcional.
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormField label="Tipo de chave" error={errors.pix_key_type}>
          <FormSelect
            value={form.pix_key_type}
            onChange={(type) => update({ pix_key_type: type as PixKeyType | '', pix_key: '' })}
            options={PIX_KEY_TYPE_OPTIONS}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Chave" error={errors.pix_key} isRequired={Boolean(form.pix_key_type)}>
          <MaskedInput
            mask={form.pix_key_type ? PIX_KEY_MASKS[form.pix_key_type] : null}
            value={form.pix_key}
            onChange={(pixKey) => update({ pix_key: pixKey })}
            isDisabled={isDisabled || !form.pix_key_type}
          />
        </FormField>
      </SimpleGrid>

      <SectionTitle mt={8}>Sobre você</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormField label="Estado civil" error={errors.marital_status} isRequired={required}>
          <FormSelect
            value={form.marital_status}
            onChange={(value) => update({ marital_status: value as typeof form.marital_status })}
            options={MARITAL_STATUS_OPTIONS}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Cor/Raça" error={errors.race} isRequired={required}>
          <FormSelect
            value={form.race}
            onChange={(value) => update({ race: value as typeof form.race })}
            options={RACE_OPTIONS}
            isDisabled={isDisabled}
          />
        </FormField>
        <FormField label="Possui filhos?" error={errors.has_children} isRequired={required} htmlFor="has_children">
          <RadioGroup
            id="has_children"
            value={form.has_children}
            onChange={(value) => update({ has_children: value as 'yes' | 'no' })}
            isDisabled={isDisabled}
          >
            <Stack direction="row" spacing={6} minH="40px" align="center">
              <Radio value="yes">Sim</Radio>
              <Radio value="no">Não</Radio>
            </Stack>
          </RadioGroup>
        </FormField>
        {form.has_children === 'yes' && (
          <FormField label="Quantidade de filhos(as)" error={errors.children_count} isRequired={required}>
            <Input
              type="number"
              min={1}
              max={30}
              value={form.children_count}
              onChange={(e) => update({ children_count: e.target.value })}
              isDisabled={isDisabled}
            />
          </FormField>
        )}
      </SimpleGrid>

      <SectionTitle mt={8}>Foto de perfil</SectionTitle>
      <Box maxW="240px">
        <ImageCropInput
          value={photo ?? photoUrl}
          onChange={onPhotoChange}
          aspectRatio={1}
          accept="photo"
          maxOutputSize={PHOTO_SIZE}
          previewAspectRatio="1 / 1"
          label="Foto"
          isRequired={required}
          isDisabled={isDisabled}
          error={errors.photo}
        />
      </Box>
    </>
  )
}
